/**
 * Native auth API client.
 *
 * Token storage lives in lib/authToken.ts (localStorage); this module is
 * the typed wrapper around /v1/auth/*.
 */
import { apiFetch } from './api';
import { clearTokens, getRefreshToken, setTokens } from './authToken';

export interface AuthUser {
  id: string;
  email: string;
  display_name: string | null;
  role: string;
  is_verified: boolean;
  /** UI language for auth emails ('en' | 'es'). Absent on old cached users. */
  locale?: string;
}

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: AuthUser;
}

function isAuthUser(value: unknown): value is AuthUser {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.email === 'string' &&
    (v.display_name === null || typeof v.display_name === 'string') &&
    typeof v.role === 'string' &&
    typeof v.is_verified === 'boolean'
  );
}

export function isTokenPair(value: unknown): value is TokenPair {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.access_token === 'string' &&
    typeof v.refresh_token === 'string' &&
    typeof v.expires_in === 'number' &&
    isAuthUser(v.user)
  );
}

export async function registerAccount(
  email: string,
  password: string,
  displayName: string,
  locale: string,
): Promise<{ message: string }> {
  const payload = await apiFetch<{ message?: unknown }>('/v1/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, display_name: displayName, locale }),
  });
  return {
    message:
      typeof payload.message === 'string'
        ? payload.message
        : 'Account created. Check your email for a verification link.',
  };
}

export async function verifyEmail(token: string): Promise<{ verified: boolean; email: string }> {
  const payload = await apiFetch<{ verified?: unknown; email?: unknown }>('/v1/auth/verify', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
  return { verified: payload.verified === true, email: typeof payload.email === 'string' ? payload.email : '' };
}

export async function login(email: string, password: string): Promise<TokenPair> {
  const payload = await apiFetch<unknown>('/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  if (!isTokenPair(payload)) throw new Error('Malformed login response');
  setTokens(payload.access_token, payload.refresh_token);
  return payload;
}

/**
 * Sign in to this city's staff account. The session only works on this
 * city's site; residents use `login` instead.
 */
export async function staffLogin(email: string, password: string): Promise<TokenPair> {
  const payload = await apiFetch<unknown>('/v1/auth/staff/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  if (!isTokenPair(payload)) throw new Error('Malformed login response');
  setTokens(payload.access_token, payload.refresh_token);
  return payload;
}

/** Staff and admin roles can use the staff dashboard. */
export function isStaffRole(role: string | undefined | null): boolean {
  return role === 'staff' || role === 'admin';
}

/** Ask for a fresh verification link. Always succeeds (no enumeration). */
export async function resendVerification(email: string): Promise<void> {
  await apiFetch<unknown>('/v1/auth/verify/resend', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function fetchMe(): Promise<AuthUser> {
  const payload = await apiFetch<unknown>('/v1/auth/me');
  if (!isAuthUser(payload)) throw new Error('Malformed profile response');
  return payload;
}

/**
 * Persist the account's UI-language preference (en/es). Best-effort:
 * auth emails follow the stored value, but the switcher must never block
 * on the network.
 */
export async function updateLocale(locale: string): Promise<void> {
  await apiFetch<unknown>('/v1/auth/me', {
    method: 'PATCH',
    body: JSON.stringify({ locale }),
  });
}

/** Best-effort server-side logout; always clears local tokens. */
export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    try {
      await apiFetch<unknown>('/v1/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch {
      // Session is over locally regardless.
    }
  }
  clearTokens();
}

export async function requestPasswordReset(
  email: string,
  options: { staff?: boolean } = {},
): Promise<{ message: string }> {
  const payload = await apiFetch<{ message?: unknown }>('/v1/auth/password-reset/request', {
    method: 'POST',
    body: JSON.stringify({ email, staff: options.staff === true }),
  });
  return {
    message:
      typeof payload.message === 'string'
        ? payload.message
        : 'If an account exists for that email, a reset link is on its way.',
  };
}

export async function confirmPasswordReset(token: string, newPassword: string): Promise<{ message: string }> {
  const payload = await apiFetch<{ message?: unknown }>('/v1/auth/password-reset/confirm', {
    method: 'POST',
    body: JSON.stringify({ token, new_password: newPassword }),
  });
  return {
    message:
      typeof payload.message === 'string' ? payload.message : 'Password updated. Sign in with your new password.',
  };
}
